import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import HLS from 'hls.js';
import styled from 'styled-components';
import Plyr from 'plyr';

import 'plyr/dist/plyr.css';

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;

  .plyr__poster {
    background-color: transparent;
  }
`;

const playerConfig = {
  captions: { active: true, update: true, language: 'en' },
  controls: [
    'play-large', // The large play button in the center
    'restart', // Restart playback
    'play', // Play/pause playback
    'progress', // The progress bar and scrubber for playback and buffering
    'current-time', // The current time of playback
    'duration', // The full duration of the media
    'mute', // Toggle mute
    'volume', // Volume control
    'fullscreen', // Toggle fullscreen
  ],
};

const VideoPlayer = ({ src, posterSrc }) => {
  const [plyrInstance, setPlyrInstance] = useState(null);
  const videoRef = useRef();

  const hlsSupported = useMemo(() => {
    return HLS.isSupported();
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      setPlyrInstance(new Plyr(videoRef.current, playerConfig));
      videoRef.current.disablePictureInPicture = true;
    }
  }, [videoRef]);

  useEffect(() => {
    if (plyrInstance) {
      plyrInstance.poster = posterSrc;
    }
  }, [plyrInstance, posterSrc]);

  useEffect(() => {
    let hls;
    if (hlsSupported && videoRef.current) {
      hls = new HLS();
      hls.attachMedia(videoRef.current);

      hls.once(HLS.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });
    }

    return () => {
      if (hls) {
        hls.detachMedia();
        hls.destroy();
      }
    };
  }, [videoRef, hlsSupported, src]);

  if (!hlsSupported) {
    return null;
  }

  return (
    <VideoWrapper>
      <video ref={videoRef} controls />
    </VideoWrapper>
  );
};

VideoPlayer.propTypes = {
  src: PropTypes.string,
  posterSrc: PropTypes.string,
};

export default VideoPlayer;
